import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { t } from '../../services/i18nService';
import modalStyles from '../../styles/generic/modal.scss';
import alertStyles from './alertTemplate.scss';
import EssayRestoreScrim from './EssayRestoreScrim';
import EssayRestoreStatusIcon from './EssayRestoreStatusIcon';
import useEssayRestoreModalFocus, { ESSAY_RESTORE_CARD_ATTRIBUTE } from './useEssayRestoreModalFocus';

/**
 * Restore card shown when the essay blanks. A modal (AC1.7): see EssayRestoreScrim and
 * useEssayRestoreModalFocus. No `styles` prop: an inline position would break the centring.
 */
const EssayRestoreAlert = ({ model, extraClasses, handleClose, id }) => {
    const [isRestoring, setIsRestoring] = useState(false);
    const isMountedRef = useRef(true);
    const cardRef = useRef(null);

    useEssayRestoreModalFocus(cardRef);
    const focusCard = () => {
        if (cardRef.current) {
            cardRef.current.focus();
        }
    };

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const onRestoreClick = () => {
        if (isRestoring) {
            return;
        }

        // Keep focus in the card while the button is disabled.
        focusCard();
        setIsRestoring(true);

        Promise.resolve(typeof model.onRestore === 'function' ? model.onRestore() : false)
            .then((restored) => {
                if (!isMountedRef.current) {
                    return;
                }
                if (restored) {
                    handleClose();
                } else {
                    // Restore did not go through - let the candidate try again.
                    setIsRestoring(false);
                }
            })
            .catch(() => {
                if (isMountedRef.current) {
                    setIsRestoring(false);
                }
            });
    };

    const onCancelClick = () => {
        // Record the dismissal before closing (see EssayRestoreService.dismissPrompt).
        if (typeof model.onCancel === 'function') {
            model.onCancel();
        }
        if (typeof handleClose === 'function') {
            handleClose();
        }
    };

    return (
        <div
            className={`${extraClasses} ${alertStyles.essayRestoreAlert}`}
            id={id}
            role="dialog"
            aria-modal="true"
            aria-labelledby="essayRestoreCardTitle"
            aria-busy={isRestoring}
            tabIndex="-1"
            ref={cardRef}
            {...{ [ESSAY_RESTORE_CARD_ATTRIBUTE]: true }}
        >
            <EssayRestoreScrim />
            {/* Stable id for the visual specs. */}
            <div
                id="essayRestoreCard"
                className={`${modalStyles.modalContentWrapper} ${alertStyles.essayRestoreAlertCard}`}
            >
                <h3
                    id="essayRestoreCardTitle"
                    className={`${modalStyles.modalTitle} ${alertStyles.essayRestoreAlertHeader}`}
                >
                    <EssayRestoreStatusIcon type="warning" className={alertStyles.essayRestoreAlertWarningIcon} />
                    <span dangerouslySetInnerHTML={{ __html: t('contentRestore.title') }} />
                </h3>

                <div className={`${modalStyles.modalContent} ${alertStyles.essayRestoreAlertBody}`}>
                    <p className={modalStyles.modalContentPart}>
                        <span dangerouslySetInnerHTML={{ __html: t('contentRestore.alert.message') }} />
                    </p>
                </div>

                <div className={`${modalStyles.modalFooterButtons} ${alertStyles.essayRestoreAlertActions}`}>
                    {/* Hidden while restoring, per the design. */}
                    {!isRestoring &&
                        <button
                            type="button"
                            id="essayRestoreCancelBtn"
                            className={`btn default-btn ${alertStyles.essayRestoreAlertCancelButton}`}
                            onClick={onCancelClick}
                        >
                            {t('general.cancel')}
                        </button>
                    }
                    {/* modalMainBtn, not main-btn: they differ in the dark contrast options. */}
                    <button
                        type="button"
                        id="essayRestoreConfirmBtn"
                        className={`btn default-btn ${modalStyles.modalMainBtn} ${alertStyles.essayRestoreAlertButton}`}
                        onClick={onRestoreClick}
                        disabled={isRestoring}
                    >
                        {isRestoring && <i className={`fa fa-spinner fa-spin ${alertStyles.essayRestoreAlertSpinner}`} aria-hidden="true" />}
                        {isRestoring
                            ? t('contentRestore.restoring')
                            : t('contentRestore.alert.restoreButton')}
                    </button>
                </div>
            </div>
        </div>
    );
};

EssayRestoreAlert.propTypes = {
    model: PropTypes.object.isRequired,
    extraClasses: PropTypes.string,
    handleClose: PropTypes.func,
    id: PropTypes.string,
};

export default EssayRestoreAlert;
